import { SessionContext } from '../../context/session-context'
import { useQuery } from '../../hook/use-query'
import { authService } from '../../service/auth-service'
import { config } from '../../util/config'
import { logger } from '../../util/logger'
import { ErrorMessageHint } from '../util/error-message-hint'
import { Logo } from '../util/logo'
import { PasswordToggle } from '../util/password-toggle'
import { OverlaySpinner } from '../util/spinner/overlay-spinner'
import styles from './login-form.module.scss'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useContext, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import * as yup from 'yup'

type FormValues = {
  email: string
  password: string
}

const loginSchema = yup
  .object()
  .shape({
    email: yup.string().email('Please enter a valid email address').trim().required('Email is required.'),
    password: yup.string().trim().required('Password is required.'),
  })
  .required()

export const LoginForm = (): React.ReactElement => {
  const query = useQuery()
  const [errorMessage, setErrorMessage] = useState<string>()
  const [passwordVisible, setPasswordVisible] = useState(false)
  const { redirectUrl } = useContext(SessionContext)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ defaultValues: { email: query.get('email') ?? '' }, resolver: yupResolver(loginSchema) })

  const [isLoading, setIsLoading] = useState(false)

  const onSubmit = async (data: FormValues): Promise<void> => {
    try {
      setIsLoading(true)
      setErrorMessage(undefined)
      const { email, password } = data
      await authService.login({ email, password, redirectUrl })
    } catch (err: any) {
      logger.error(err)
      setErrorMessage(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAppleSignIn = async (): Promise<void> => {
    try {
      setIsLoading(true)
      setErrorMessage(undefined)
      const baseUrl = window.location.origin
      await authService.signInWithApple({ redirectUrl: `${baseUrl}/auth/code/callback` })
    } catch (err: any) {
      logger.error(err)
      setErrorMessage(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <div className={`${styles.logoContainer} text-center`}>
        <Logo />
      </div>
      <Form.Group className={styles.inputContainerGroup}>
        <h1 className="text-center">LOGIN</h1>
        <Form.Group>
          <Form.Control
            type="text"
            placeholder="Email"
            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
            {...register('email')}
          />
          <Form.Control.Feedback type="invalid">{errors.email && <p>{errors.email.message}</p>}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group>
          <PasswordToggle onToggle={setPasswordVisible}>
            <Form.Control
              type={passwordVisible ? 'text' : 'password'}
              placeholder="Password"
              className={`form-control ${errors.password ? 'is-invalid' : ''}`}
              {...register('password', {
                required: 'You must specify a password',
              })}
            />
            <Form.Control.Feedback type="invalid">{errors.password && <p>{errors.password.message}</p>}</Form.Control.Feedback>
          </PasswordToggle>
          <small id="forgotPassword" className="hint form-text text-muted">
            <strong>
              <Link className="text-secondary" to="/forgot-password">
                Forgotten password?
              </Link>
            </strong>
          </small>
        </Form.Group>
        <ErrorMessageHint message={errorMessage} />
      </Form.Group>
      <Form.Group>
        <Button type="submit" className="w-100 mb-3" disabled={isLoading}>
          continue
        </Button>
        {config.appleSignInEnabled && (
          <Button type="button" className="w-100 mb-3" variant="dark" onClick={handleAppleSignIn} disabled={isLoading}>
            Sign in with Apple
          </Button>
        )}
        <div className="text-secondary text-center">
          Don't have an account?{' '}
          <strong>
            <u>
              <Link className="text-secondary" to="/create-account">
                Create Account
              </Link>
            </u>
          </strong>
        </div>
      </Form.Group>
      {isLoading && <OverlaySpinner />}
    </Form>
  )
}
